import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { AccountType } from '@scrambl/shared'
import { useAccountType } from '@/hooks/useAccountType'
import { useOrganization } from '@/hooks/useOrganization'

interface AccountTypeGuardProps {
  /** Account types allowed to view the wrapped route. */
  allowed: AccountType[]
  children: ReactNode
}

/**
 * Route guard that blocks a route unless the current organization's account
 * type is in `allowed`. Used for surfaces feature flags can't express — notably
 * billing/subscription, which personal accounts have no flag row for. Redirects
 * to the org's today page when disallowed. `useAccountType` defaults to
 * ORGANIZATION until the org loads, so org routes never flash a redirect.
 */
export function AccountTypeGuard({ allowed, children }: AccountTypeGuardProps) {
  const { accountType } = useAccountType()
  const { currentOrganization } = useOrganization()

  if (!allowed.includes(accountType)) {
    const orgSlug = currentOrganization?.slug ?? ''
    return <Navigate to={`/${orgSlug}/today`} replace />
  }

  return <>{children}</>
}
